import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { fetchHubupCommunitiesForEnterpriseAssign } from '../../api/enterprises';
import {
  createHubupAdminGroup,
  updateHubupAdminGroup,
} from '../../api/hubup';

function matchesSearch(c, q) {
  if (!q) return true;
  const hay = `${c?.name ?? ''} ${c?.id ?? ''}`.toLowerCase();
  return hay.includes(q);
}

export default function HubupCommunityAssignPanel({
  linkedCommunityId,
  onLinkCommunity,
  linking,
  linkError,
  linkMessage,
}) {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState('');
  const [formIsPro, setFormIsPro] = useState(false);
  const [formNotificationLeft, setFormNotificationLeft] = useState('0');
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await fetchHubupCommunitiesForEnterpriseAssign();
      setCommunities(res?.data?.communities || []);
    } catch (e) {
      setLoadError(e?.message || 'Chargement impossible.');
      setCommunities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const filtered = useMemo(
    () => communities.filter((c) => matchesSearch(c, query)),
    [communities, query],
  );

  const linked = String(linkedCommunityId || '').trim();
  const linkedCommunity = communities.find((c) => c.id === linked);

  const openCreate = () => {
    setEditing(null);
    setFormName('');
    setFormIsPro(false);
    setFormNotificationLeft('0');
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setFormName(String(c?.name ?? ''));
    setFormIsPro(c?.isPro === true);
    setFormNotificationLeft(String(Math.max(0, Number(c?.notificationLeft) || 0)));
    setFormError('');
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const name = formName.trim();
    if (!name) return;
    setFormSaving(true);
    setFormError('');
    try {
      const payload = {
        name,
        isPro: formIsPro,
        notificationLeft: Math.max(0, Number.parseInt(String(formNotificationLeft), 10) || 0),
      };
      if (editing?.id) {
        await updateHubupAdminGroup(editing.id, payload);
      } else {
        const res = await createHubupAdminGroup(payload);
        const created = res?.data?.community ?? res?.data?.group;
        if (created?.id && onLinkCommunity) {
          await onLinkCommunity(String(created.id));
        }
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setFormError(err?.message || 'Enregistrement impossible.');
    } finally {
      setFormSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-foreground">Lier une communauté HubUP</p>
        <Button type="button" size="sm" variant="outline" onClick={openCreate}>
          Créer
        </Button>
      </div>

      {linked ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Actuelle :</span>
          <span className="font-medium">{linkedCommunity?.name || linked}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={linking}
            onClick={() => onLinkCommunity?.('')}
          >
            Retirer
          </Button>
        </div>
      ) : null}

      <Input
        label="Recherche"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Nom ou identifiant"
      />

      {loadError ? <p className="text-xs text-destructive">{loadError}</p> : null}
      {linkError ? <p className="text-xs text-destructive">{linkError}</p> : null}
      {linkMessage ? <p className="text-xs text-emerald-600">{linkMessage}</p> : null}

      <div className="max-h-64 overflow-y-auto space-y-1">
        {loading ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Chargement…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Aucune communauté.</p>
        ) : (
          filtered.map((c) => {
            const isLinked = c.id === linked;
            return (
              <div
                key={c.id}
                className={`flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm ${
                  isLinked ? 'bg-primary/10' : 'hover:bg-muted/50'
                }`}
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{c.name}</p>
                  <p className="text-[10px] text-muted-foreground font-mono truncate">{c.id}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button type="button" variant="ghost" size="sm" onClick={() => openEdit(c)}>
                    Modifier
                  </Button>
                  {!isLinked ? (
                    <Button
                      type="button"
                      size="sm"
                      disabled={linking}
                      onClick={() => onLinkCommunity?.(c.id)}
                    >
                      Lier
                    </Button>
                  ) : (
                    <span className="text-xs text-primary px-2 py-1">Liée</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showForm ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 p-4">
          <form
            onSubmit={handleFormSubmit}
            className="bg-card rounded-lg border border-border w-full max-w-sm p-4 space-y-3"
            role="dialog"
            aria-modal="true"
          >
            <p className="text-sm font-medium">{editing ? 'Modifier' : 'Nouvelle communauté'}</p>
            <Input label="Nom" value={formName} onChange={(e) => setFormName(e.target.value)} required />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={formIsPro} onChange={(e) => setFormIsPro(e.target.checked)} />
              Pro
            </label>
            <Input
              label="Notifications restantes"
              type="number"
              min={0}
              value={formNotificationLeft}
              onChange={(e) => setFormNotificationLeft(e.target.value)}
            />
            {formError ? <p className="text-xs text-destructive">{formError}</p> : null}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                Annuler
              </Button>
              <Button type="submit" size="sm" disabled={formSaving}>
                {formSaving ? '…' : 'OK'}
              </Button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
